import { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Alert, Chip } from '@mui/material';
import { fetchWithAuth } from '../../api/backendApi';
import { 
  CheckCircle as CheckCircleIcon, 
  AddCircle as AddCircleIcon,
  Assignment as AssignmentIcon,
  PlayArrow as PlayArrowIcon,
  Note as NoteIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';

const EVENT_ICONS = {
  INCIDENT_CREATED: <AddCircleIcon fontSize="small" />,
  PANEL_ASSIGNED: <AssignmentIcon fontSize="small" />,
  ACTION_REQUESTED: <PlayArrowIcon fontSize="small" />,
  NOTE_ADDED: <NoteIcon fontSize="small" />,
  RESOLVED: <CheckCircleIcon fontSize="small" />,
  PANEL_OPENED: <OpenInNewIcon fontSize="small" />
};

const EVENT_COLORS = {
  INCIDENT_CREATED: 'primary',
  PANEL_ASSIGNED: 'info',
  ACTION_REQUESTED: 'warning',
  NOTE_ADDED: 'default',
  RESOLVED: 'success',
  PANEL_OPENED: 'secondary'
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);

  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const EventItem = ({ event }) => {
  const icon = EVENT_ICONS[event.type] || <NoteIcon fontSize="small" />;
  const color = EVENT_COLORS[event.type] || 'default';

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        mb: 2,
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': {
          borderBottom: 'none',
          mb: 0,
          pb: 0
        }
      }}
    >
      <Box sx={{ pt: 0.5 }}>
        <Chip
          icon={icon}
          label={event.type.replace(/_/g, ' ')}
          size="small"
          color={color}
          sx={{ fontSize: '0.7rem' }}
        />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
          {getEventDescription(event)}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {event.actor?.email || event.actor?.userId || 'System'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            •
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatTimestamp(event.createdAt)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const getEventDescription = (event) => {
  const payload = event.payload || {};

  switch (event.type) {
    case 'INCIDENT_CREATED':
      return `Incident created: ${payload.incidentType || 'Unknown type'}`;
    
    case 'PANEL_ASSIGNED':
      return `Panel assigned to ${payload.target?.kind === 'role' ? 'role' : 'user'}: ${payload.target?.value}`;
    
    case 'ACTION_REQUESTED':
      return `Action: ${payload.actionLabel || payload.actionType || 'Unknown action'}`;
    
    case 'NOTE_ADDED':
      return payload.note || 'Note added';
    
    case 'RESOLVED':
      return `Incident resolved${payload.note ? ': ' + payload.note : ''}`;
    
    case 'PANEL_OPENED':
      return `Panel opened: ${payload.panelKey || 'Unknown panel'}`;
    
    default:
      return event.type.replace(/_/g, ' ');
  }
};

const IncidentTimeline = ({ incidentId, tenantId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!incidentId || !tenantId) {
      setLoading(false);
      return;
    }

    console.log('[INCIDENT] Polling events for incident:', incidentId);

    const loadEvents = async () => {
      try {
        const response = await fetchWithAuth(
          `/api/v2/tenants/${tenantId}/incidents/${incidentId}/events?limit=10`,
          'GET'
        );

        const nextEvents = response?.data?.events || response?.events;
        if (Array.isArray(nextEvents)) {
          console.log('[INCIDENT] Events updated:', nextEvents.length);
          setEvents(nextEvents);
          setError(null);
        } else {
          setEvents([]);
        }
        setLoading(false);
      } catch (err) {
        console.error('[INCIDENT] Error loading events:', err);
        setError(err.message || 'Failed to load events');
        setLoading(false);
      }
    };

    // Initial load
    loadEvents();

    // Poll every 3 seconds for updates
    const interval = setInterval(loadEvents, 3000);

    return () => clearInterval(interval);
  }, [incidentId, tenantId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load timeline: {error}
      </Alert>
    );
  }

  if (events.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        No events yet
      </Typography>
    );
  }

  return (
    <Box sx={{ maxHeight: 400, overflowY: 'auto', pr: 1 }}>
      {events.map((event) => (
        <EventItem key={event.eventId} event={event} />
      ))}
    </Box>
  );
};

export default IncidentTimeline;
